/**
 * CheckoutModal - Embedded Stripe Checkout
 * Displays checkout form in a modal matching the app's dark theme
 * Uses React Portal to render directly to document.body for proper positioning
 */

import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';
import { X, Tag, ArrowRight, Check } from 'lucide-react';
import { createEmbeddedCheckoutSession, getCheckoutOptions, clearStoredCodes, getStoredPromoCode } from '../services/stripe';
import { devLog, devError } from '../utils/debugLogger';
import { useApp } from '../context/AppContext';
import { getColors, getModalStyle, fonts } from '../theme';

// Initialize Stripe with your publishable key
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
if (!stripeKey) {
  devError('Stripe publishable key is missing. Make sure REACT_APP_STRIPE_PUBLISHABLE_KEY is set in your environment.');
}
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

/**
 * CheckoutModal Component
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Callback to close the modal
 * @param {string} props.priceType - 'monthly' or 'yearly'
 * @param {string} props.userId - Firebase user ID
 * @param {string} props.userEmail - User's email
 * @param {function} props.onSuccess - Callback when checkout succeeds (optional)
 */
const CheckoutModal = ({ isOpen, onClose, priceType, userId, userEmail, onSuccess }) => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  
  // Promo code state - show input first, then checkout
  const [promoCode, setPromoCode] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);
  const [promoApplied, setPromoApplied] = useState(false);
  
  // Ref to capture promo code at checkout time (avoids stale closure issues)
  const promoCodeRef = React.useRef('');
  
  // Ref for overlay scroll control
  const overlayRef = React.useRef(null);
  
  // Ensure we're mounted before using portal
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);
  
  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Check if there's a stored promo code from URL
      const storedPromo = getStoredPromoCode();
      if (storedPromo) {
        setPromoCode(storedPromo);
        setPromoApplied(true);
      } else {
        setPromoCode('');
        setPromoApplied(false);
      }
      setShowCheckout(false);
      setError(null);
      setLoading(false);
    }
  }, [isOpen]);

  // Scroll overlay to top when modal opens
  useLayoutEffect(() => {
    if (isOpen && overlayRef.current) {
      overlayRef.current.scrollTop = 0;
    }
  }, [isOpen, loading]);

  // Fetch client secret for embedded checkout
  const fetchClientSecret = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Use ref to get the promo code captured at checkout time (avoids stale closure)
      const capturedPromoCode = promoCodeRef.current || null;
      const checkoutOptions = getCheckoutOptions({ promoCode: capturedPromoCode });
      devLog('[Checkout] Using options:', checkoutOptions);
      const { clientSecret } = await createEmbeddedCheckoutSession(priceType, userId, userEmail, checkoutOptions);
      setLoading(false);
      return clientSecret;
    } catch (err) {
      devError('Error fetching client secret:', err);
      setError(err.message || 'Failed to initialize checkout');
      setLoading(false);
      throw err;
    }
  }, [priceType, userId, userEmail]);
  
  // Handle proceeding to checkout
  const handleProceedToCheckout = () => {
    // Capture promo code in ref before showing checkout
    promoCodeRef.current = promoCode.trim();
    devLog('[Checkout] Proceeding with promo code:', promoCodeRef.current || '(none)');
    setShowCheckout(true);
    // Let EmbeddedCheckoutProvider handle its own loading state
  };

  // Handle successful checkout
  const handleComplete = useCallback(() => {
    // Clear stored promo/affiliate codes after successful checkout
    clearStoredCodes();
    // The checkout completed - user will be redirected via return_url
    // But we can also trigger onSuccess if provided
    if (onSuccess) {
      onSuccess();
    }
  }, [onSuccess]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll and scroll overlay to top when modal opens
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      // Scroll overlay to top
      if (overlayRef.current) {
        overlayRef.current.scrollTop = 0;
      }
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    }
    return () => {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    };
  }, [isOpen]);

  if (!isOpen || !mounted) return null;

  // Stripe Embedded Checkout options
  // Note: EmbeddedCheckout doesn't support appearance/theming - it uses Stripe's hosted UI
  // The checkout form will always be light-themed for trust/security reasons
  const options = {
    fetchClientSecret,
    onComplete: handleComplete,
  };

  const modalContent = (
    <div 
      ref={overlayRef}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
        <div 
          className="relative w-full max-w-lg rounded-lg overflow-hidden flex flex-col animate-scale-up"
          style={{ 
            ...getModalStyle(colors),
            maxHeight: '90vh'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div 
            className="flex items-center justify-between p-3 sm:p-4 border-b"
            style={{ borderColor: colors.border, backgroundColor: colors.bgSecondary }}
          >
            <h2 
              className="text-base sm:text-lg font-medium"
              style={{ 
                color: colors.textPrimary,
                fontFamily: "'Noto Serif', Georgia, serif"
              }}
            >
              Complete Your Subscription
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition-all hover:opacity-80 flex-shrink-0"
              style={{ 
                backgroundColor: colors.bgTertiary,
                color: colors.textSecondary 
              }}
            >
              <X size={18} />
            </button>
          </div>

          {/* Content */}
          <div className="p-3 sm:p-4 overflow-y-auto flex-1">
            {error ? (
              <div 
                className="p-4 rounded-md text-center"
                style={{ backgroundColor: 'rgba(248, 113, 113, 0.1)' }}
              >
                <p style={{ color: colors.error }}>{error}</p>
                <button
                  onClick={() => {
                    setError(null);
                    setShowCheckout(false);
                    setLoading(false);
                  }}
                  className="mt-3 px-4 py-2 rounded-md font-medium"
                  style={{ 
                    backgroundColor: colors.accent,
                    color: colors.onAccent
                  }}
                >
                  Try Again
                </button>
              </div>
            ) : !showCheckout ? (
              /* Promo Code Input Step */
              <div className="space-y-4">
                <div className="text-center mb-4">
                  <p style={{ color: colors.textSecondary, fontSize: '0.9rem' }}>
                    {priceType === 'yearly' ? 'Yearly Plan: $50/year' : 'Monthly Plan: $5/month'}
                  </p>
                </div>
                
                {/* Promo Code Input */}
                <div>
                  <label 
                    className="block text-sm font-medium mb-2"
                    style={{ color: colors.textSecondary }}
                  >
                    <Tag size={14} className="inline mr-2" />
                    Have a promo code?
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => {
                        setPromoCode(e.target.value.toUpperCase());
                        setPromoApplied(false);
                      }}
                      placeholder="Enter code (optional)"
                      className="flex-1 px-4 py-3 rounded-md text-sm transition-all"
                      style={{
                        backgroundColor: colors.bgTertiary,
                        border: `1px solid ${promoApplied ? colors.accent : colors.border}`,
                        color: colors.textPrimary,
                        outline: 'none',
                      }}
                      onFocus={(e) => e.target.style.borderColor = colors.accent}
                      onBlur={(e) => e.target.style.borderColor = promoApplied ? colors.accent : colors.border}
                    />
                    {promoApplied && (
                      <div 
                        className="flex items-center justify-center px-3 rounded-md"
                        style={{ backgroundColor: `${colors.accent}20`, color: colors.accent }}
                      >
                        <Check size={18} />
                      </div>
                    )}
                  </div>
                  <p 
                    className="text-xs mt-1.5"
                    style={{ color: colors.textMuted }}
                  >
                    Leave blank if you don't have one
                  </p>
                </div>
                
                {/* Proceed Button */}
                <button
                  onClick={handleProceedToCheckout}
                  className="w-full py-3.5 rounded-md font-medium flex items-center justify-center gap-2 transition-all hover:opacity-90"
                  style={{
                    backgroundColor: colors.accent,
                    color: colors.onAccent,
                    fontFamily: fonts.sans,
                  }}
                >
                  Continue to Payment
                  <ArrowRight size={18} />
                </button>
                
                <p 
                  className="text-center text-xs"
                  style={{ color: colors.textMuted }}
                >
                  You can cancel anytime from Settings
                </p>
              </div>
            ) : null}

            {/* Embedded Checkout */}
            {showCheckout && !error && (
              <div 
                id="checkout-container"
                className="stripe-checkout-wrapper"
                style={{ minHeight: '300px' }}
              >
                <EmbeddedCheckoutProvider
                  stripe={stripePromise}
                  options={options}
                >
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </div>
            )}
          </div>

          {/* Footer hint */}
          <div 
            className="p-2 sm:p-3 text-center text-xs border-t"
            style={{ 
              borderColor: colors.border,
              color: colors.textMuted,
              backgroundColor: colors.bgSecondary
            }}
          >
            Secured by Stripe • Cancel anytime
          </div>
        </div>
    </div>
  );

  // Use portal to render directly to document.body, bypassing any parent transforms
  return createPortal(modalContent, document.body);
};

export default CheckoutModal;
